import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

const resolveSrc = (item, folder) => {
  if (item.src) {
    return item.src;
  }
  if (folder && item.file) {
    return `${folder.replace(/\/$/, '')}/${item.file}`;
  }
  return null;
};

const shouldBypassHead = (url) => /^https?:\/\//i.test(url);

const EventMediaGallery = ({ media = [], folder }) => {
  const [availableMedia, setAvailableMedia] = useState([]);

  useEffect(() => {
    if (!media.length) {
      setAvailableMedia([]);
      return;
    }

    let isMounted = true;

    const loadMedia = async () => {
      const results = await Promise.all(
        media.map(async (item) => {
          const resolvedSrc = resolveSrc(item, folder);
          if (!resolvedSrc) {
            return null;
          }

          if (shouldBypassHead(resolvedSrc)) {
            return { ...item, src: resolvedSrc };
          }

          try {
            const response = await fetch(resolvedSrc, { method: 'HEAD' });
            if (!isMounted) {
              return null;
            }
            return response.ok ? { ...item, src: resolvedSrc } : null;
          } catch {
            return null;
          }
        })
      );

      if (isMounted) {
        setAvailableMedia(results.filter(Boolean));
      }
    };

    loadMedia();

    return () => {
      isMounted = false;
    };
  }, [folder, media]);

  if (!availableMedia.length) {
    return null;
  }

  return (
    <Stack spacing={1.5} mt={2}>
      <Typography variant="subtitle2" color="text.secondary">
        Event gallery
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 1,
          '&::-webkit-scrollbar': {
            height: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(120,120,120,0.35)',
            borderRadius: 999,
          },
        }}
      >
        {availableMedia.map((item) => (
          <Box
            key={item.src}
            component="img"
            src={item.src}
            alt={item.alt || 'Event highlight'}
            loading="lazy"
            sx={{
              height: 160,
              width: 240,
              objectFit: 'cover',
              borderRadius: 3,
              border: '1px solid rgba(15,15,20,0.08)',
              boxShadow: '0 10px 24px rgba(20, 20, 30, 0.12)',
              flexShrink: 0,
            }}
          />
        ))}
      </Box>
    </Stack>
  );
};

export default EventMediaGallery;
